import EventSection from '../Messages/Sections/EventSection';
import { ENTITY_COLORS } from '../../appearance/Colors';
import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { FiCheck, FiCheckSquare, FiExternalLink, FiMapPin, FiSquare, FiX } from 'react-icons/fi';

import { getArtifactEntryEmoji, getArtifactSectionEmoji, getArtifactMetadataEmoji } from './artifactEmoji';
import Modal from '../Modals/Modal';
import DefaultMessage from '../Messages/DefaultMessage';
import { MUTATION, QUERY } from '../../gql-operations';
import { getArtifactPresentation, getCheckableEntryIds, isCheckableSection, isListArtifact, isCompletedList, getArtifactColors, getEventTimingInfo } from './artifactPresentation';

const Emoji = ({ value }) => value ? <Decoration aria-hidden="true">{value}</Decoration> : null;

const getType = artifact => {
    const type = String(artifact?.type || '').toLowerCase();
    return type === 'repair' || type === 'procedure' ? 'tutorial' : type === 'movie' ? 'film' : type;
};

const isProcedure = artifact => ['recipe', 'tutorial', 'workout', 'packing_list', 'shopping_list', 'checklist'].includes(getType(artifact));
const STATE_OPTIONS = {
    place: [['Want to go', 'WANT'], ['Been', 'COMPLETED'], ['Lived', 'COMPLETED', ['LIVED']]],
    recipe: [['Want to cook', 'WANT'], ['Cooking', 'IN_PROGRESS'], ['Cooked', 'COMPLETED'], ['Meal planned', 'WANT', ['MEAL_PLANNED']]],
    film: [['Want to watch', 'WANT'], ['Watching', 'IN_PROGRESS'], ['Watched', 'COMPLETED']],
    book: [['Want to read', 'WANT'], ['Reading', 'IN_PROGRESS'], ['Read', 'COMPLETED']]
};

const SourceMessagePreview = ({ sourceMessage, onOpenMessage }) => {
    const {t: lnT} = useTranslation();

    const sourceMessageId = String(sourceMessage?.id || '').trim();
    const { data, loading } = useQuery(QUERY.GET_MESSAGE, {
        variables: { data: { id: sourceMessageId } },
        skip: !sourceMessageId,
        fetchPolicy: 'cache-first'
    });
    const message = data?.getMessage;

    if (!message) {
        return <SourceMessageLoading>{loading ? lnT('messages:loadingSavedMessage') : lnT('common:savedMessageUnavailable')}</SourceMessageLoading>;
    }

    return <SourceMessageCard>
        <DefaultMessage
            index={0}
            screenName="artifact-library"
            message={message}
            multipleSelect={false}
            imagePreview={() => undefined}
            hideCheckboxSection
            hideEventArtifacts
            hideQuickActions
            disableThreadAttachTarget
            suppressSelectionHoverOutline
            suppressBoardHoverHighlight
            onMessageClick={() => onOpenMessage(message.id)}
        />
    </SourceMessageCard>;
};

// One artifact-detail modal for both the Library and Message Preview. The panel
// colour intentionally matches Message Preview, with a bounded image beside
// the overview and supporting details on desktop, stacking on narrow screens.
const ArtifactDetailModal = ({ artifact, onRequestClose, onOpenMessage, onStateUpdated }) => {
    const {t: lnT} = useTranslation();

    const [viewerState, setViewerState] = React.useState(null);
    const [pending, setPending] = React.useState(false);
    const [saveError, setSaveError] = React.useState(false);
    const [imageUnavailable, setImageUnavailable] = React.useState(false);
    const [updateArtifactUserState] = useMutation(MUTATION.UPDATE_ARTIFACT_USER_STATE);
    const history = useHistory();
    const currentState = viewerState || artifact?.viewerState || null;

    React.useEffect(() => {
        setViewerState(null);
        setImageUnavailable(false);
        setSaveError(false);
    }, [artifact?.id]);

    const updateState = async next => {
        if (!artifact?.id || pending) return;
        const previous = currentState;
        setPending(true);
        setSaveError(false);
        setViewerState({ ...currentState, ...next });
        try {
            const result = await updateArtifactUserState({
                variables: { data: { artifactId: artifact.id, ...next } },
                update: (cache, { data }) => {
                    const stateId = data?.updateArtifactUserState && cache.identify(data.updateArtifactUserState);
                    const artifactId = cache.identify(artifact);
                    if (artifactId && stateId) cache.modify({
                        id: artifactId,
                        fields: { viewerState: () => ({ __ref: stateId }) }
                    });
                }
            });
            setViewerState(result?.data?.updateArtifactUserState || next);
            onStateUpdated?.();
        } catch (error) {
            setViewerState(previous);
            setSaveError(true);
        } finally {
            setPending(false);
        }
    };

    if (!artifact) return null;
    const presentation = getArtifactPresentation(artifact);
    const eventTiming = getEventTimingInfo(artifact, lnT);
    const artifactColors = getArtifactColors(artifact);
    const Icon = presentation.Icon;
    const completed = currentState?.status === 'COMPLETED' || isCompletedList(artifact, currentState);
    const checked = new Set(currentState?.checkedEntryIds || []);
    const stateOptions = STATE_OPTIONS[getType(artifact)] || [];
    const isMapsMetadata = item => item.key === 'googleMapsUrl' || /google\s*maps/i.test(String(item.label || ''));
    const mapsUrl = artifact.metadata?.find(item => isMapsMetadata(item) && /^https?:\/\//i.test(String(item.value || '').trim()))?.value;
    const HIDDEN_EVENT_METADATA_KEYS = [
        'eventDate', 'sourceDate', 'startDate', 'endDate', 'startTime', 'endTime',
        'location', 'timeZone', 'timezone', 'needsReview', 'rawDate', 'provider', 'providerId', 'providerPlaceId', 'coordinates'
    ];
    const visibleMetadata = (artifact.metadata || []).filter(item => {
        if (!String(item.value || '').trim() || isMapsMetadata(item)) return false;
        if (getType(artifact) === 'place') return ['address', 'categories', 'category', 'rating', 'ratingCount', 'website', 'priceRange'].includes(item.key);
        if (getType(artifact) === 'event') return !HIDDEN_EVENT_METADATA_KEYS.includes(item.key);
        return !['provider', 'providerId', 'providerPlaceId', 'coordinates'].includes(item.key);
    });
    const openSourceMessage = messageId => {
        onRequestClose();
        if (onOpenMessage) onOpenMessage(messageId);
        else history.push(`/message/${messageId}`);
    };

    return (
        <Modal isOpen onRequestClose={onRequestClose} style={modalStyles} contentLabel={lnT('common:details', { value1: String(artifact.title) })}>
            <Panel>
                <CloseButton type="button" onClick={onRequestClose} aria-label={lnT('common:closeArtifactDetails')}><FiX size={19} /></CloseButton>
                <Media>
                    {artifact.image && !imageUnavailable ? (
                        <Cover src={artifact.image} alt="" onError={() => setImageUnavailable(true)} />
                    ) : eventTiming ? (
                        <HeroDateTile $theme={artifactColors}>
                            <HeroDateMonth $theme={artifactColors}>{eventTiming.month}</HeroDateMonth>
                            <HeroDateDay>{eventTiming.day}</HeroDateDay>
                            {eventTiming.timing ? (
                                <HeroDateTiming $isUpcoming={!eventTiming.isPast} $theme={artifactColors}>
                                    ({eventTiming.timing})
                                </HeroDateTiming>
                            ) : null}
                        </HeroDateTile>
                    ) : (
                        <Placeholder $color={presentation.color}><Icon size={36} /></Placeholder>
                    )}
                </Media>
                <Content>
                    <Overview>
                    <Type $color={presentation.color}><Icon size={14} />{presentation.label}</Type>
                    <Title>{artifact.title}</Title>
                    {saveError ? <Summary role="alert">{lnT('common:failedToSavePleaseTryAgain')}</Summary> : null}
                    {['workout', 'tutorial'].includes(getType(artifact)) || isListArtifact(artifact) ? (
                        <StatusButton type="button" $color={presentation.color} $completed={completed} disabled={pending} onClick={() => updateState({ status: completed ? 'NONE' : 'COMPLETED', checkedEntryIds: isListArtifact(artifact) ? (completed ? [] : getCheckableEntryIds(artifact)) : currentState?.checkedEntryIds || [] })}>
                            <FiCheck size={15} />{completed ? lnT('folders:completed') : lnT('common:markComplete')}
                        </StatusButton>
                    ) : null}
                    {artifact.summary ? <Summary>{artifact.summary}</Summary> : null}
                    {stateOptions.length ? <Progress><ProgressLabel>{lnT('common:yourProgress')}</ProgressLabel><StateButtons>{stateOptions.map(([label, status, contextTags = []]) => {
                        const selected = currentState?.status === status && JSON.stringify(currentState?.contextTags || []) === JSON.stringify(contextTags);
                        return <StateButton key={label} type="button" $selected={selected} disabled={pending} onClick={() => updateState({ status, contextTags, checkedEntryIds: currentState?.checkedEntryIds || [] })}>{label}</StateButton>;
                    })}</StateButtons></Progress> : null}
                    </Overview>
                    {getType(artifact) === 'event' && <EventSection
                        message={artifact.eventMessage || { id: artifact.id, title: artifact.title, event: artifact.event }}
                        readOnly={!artifact.eventMessage} />}
                    {mapsUrl ? <MetadataLink href={mapsUrl} target="_blank" rel="noopener noreferrer"><FiMapPin size={16} />{lnT('common:openInGoogleMaps')}<FiExternalLink size={14} /></MetadataLink> : null}
                    {isProcedure(artifact) && artifact.sections?.length ? <Sections>{artifact.sections.map(section => {
                        const entries = section.entries || [];
                        const sectionHasEmoji = entries.some(entry => getArtifactEntryEmoji(artifact, section, entry));
                        return (
                            <section key={section.id}>
                                {section.title ? <SectionTitle><Emoji value={getArtifactSectionEmoji(artifact, section)} />{section.title}</SectionTitle> : null}
                                {entries.map((entry, index) => {
                                    const id = String(entry?.id || `${section.id}-${index}`);
                                    const isChecked = checked.has(id);
                                    const checkable = isCheckableSection(section);
                                    const entryEmoji = getArtifactEntryEmoji(artifact, section, entry);
                                    return <Entry key={id} $checked={isChecked}>
                                        {checkable ? <CheckButton type="button" $color={presentation.color} disabled={pending} aria-label={isChecked ? lnT('common:markIncomplete') : lnT('common:markComplete')} onClick={() => {
                                            const next = new Set(checked); isChecked ? next.delete(id) : next.add(id);
                                            const entryIds = getCheckableEntryIds(artifact);
                                            const status = isListArtifact(artifact)
                                                ? (entryIds.length > 0 && entryIds.every(entryId => next.has(entryId)) ? 'COMPLETED' : next.size > 0 ? 'IN_PROGRESS' : 'NONE')
                                                : currentState?.status || 'NONE';
                                            updateState({ status, checkedEntryIds: [...next] });
                                        }}>{isChecked ? <FiCheckSquare size={17} /> : <FiSquare size={17} />}</CheckButton> : null}
                                        {sectionHasEmoji ? (
                                            <Decoration aria-hidden="true">{entryEmoji || ''}</Decoration>
                                        ) : null}
                                        <div>{entry.quantity || entry.unit ? <Quantity>{[entry.quantity, entry.unit].filter(Boolean).join(' ')}</Quantity> : null}{entry.label && entry.label !== entry.text ? <strong>{entry.label}: </strong> : null}{entry.text || entry.label}{entry.detail ? <EntryDetail>{entry.detail}</EntryDetail> : null}</div>
                                    </Entry>;
                                })}
                            </section>
                        );
                    })}</Sections> : null}
                    {visibleMetadata.length ? <Metadata>{visibleMetadata.map(item => {
                        const imdbId = item.key === 'imdbId' && /^tt\d+$/.test(String(item.value).trim()) ? String(item.value).trim() : null;
                        const website = item.key === 'website' && /^https?:\/\//i.test(String(item.value).trim()) ? String(item.value).trim() : null;
                        return <div key={`${item.key}-${item.value}`}>
                            <span><Emoji value={getArtifactMetadataEmoji(artifact, item)} />{item.key === 'releaseDate' ? lnT('common:released') : item.key === 'imdbId' ? lnT('messages:imdb') : item.label || item.key}</span>
                            <strong>
                                {imdbId ? (
                                    <ImdbLink href={`https://www.imdb.com/title/${imdbId}/`} target="_blank" rel="noopener noreferrer"><span>imdb.com/title/{imdbId}</span></ImdbLink>
                                ) : website ? (
                                    <MetadataLink href={website} target="_blank" rel="noopener noreferrer">{item.value}</MetadataLink>
                                ) : item.value}
                            </strong>
                        </div>;
                    })}</Metadata> : null}
                    {artifact.sourceMessages?.length ? <MentionedIn><ProgressLabel>{lnT('common:mentionedIn')}</ProgressLabel>{artifact.sourceMessages.map(message => <SourceMessagePreview key={message.id} sourceMessage={message} onOpenMessage={openSourceMessage} />)}</MentionedIn> : null}
                    {artifact.sourceUrls?.[0] ? <SourceLink href={artifact.sourceUrls[0]} target="_blank" rel="noopener noreferrer">{lnT('common:viewOriginalSource')}</SourceLink> : null}
                </Content>
            </Panel>
        </Modal>
    );
};

const modalStyles = { content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', width: 'min(920px, calc(100vw - 32px))', maxHeight: 'min(800px, calc(100dvh - 32px))', padding: 0, overflow: 'auto', background: '#202426', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22 }, overlay: { backgroundColor: 'rgba(0,0,0,.62)', zIndex: 2400 } };
const Panel = styled.article`
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
    align-items: start;
    color: rgba(255,255,255,.92);

    @media (max-width: 720px) { grid-template-columns: minmax(0, 1fr); }
`;
const CloseButton = styled.button`position:absolute;z-index:1;top:14px;right:14px;width:34px;height:34px;display:grid;place-items:center;border-radius:50%;border:1px solid rgba(255,255,255,.16);background:rgba(0,0,0,.3);color:white;cursor:pointer;`;
const Media = styled.div`
    min-width: 0;
    padding: 24px;
    box-sizing: border-box;

    @media (max-width: 720px) { padding: 16px; }
`;
const Overview = styled.div`min-width: 0;`;
const Cover = styled.img`
    display: block;
    width: 100%;
    height: auto;
    max-height: min(420px, 50dvh);
    object-fit: contain;
    border-radius: 12px;

    @media (max-width: 720px) { width: auto; max-width: 100%; max-height: 32dvh; margin: 0 auto; }
`;
const Placeholder = styled.div`display:grid;place-items:center;width:100%;height:200px;border-radius:12px;background:rgba(255,255,255,.07);color:${p => p.$color};`;
const HeroDateTile = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 200px;
    height: 100%;
    border-radius: 14px;
    background: ${p => p.$theme?.bg || 'rgba(160, 100, 30, 0.18)'};
    border: 1px solid ${p => p.$theme?.border || 'rgba(160, 100, 30, 0.35)'};
    box-sizing: border-box;
    padding: 24px;
    gap: 4px;
`;
const HeroDateMonth = styled.span`
    color: ${p => p.$theme?.text || '#e8b96b'};
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 2px;
`;
const HeroDateDay = styled.span`
    color: #ffffff;
    font-size: 52px;
    font-weight: 800;
    line-height: 1;
`;
const HeroDateTiming = styled.span`
    color: ${p => (p.$isUpcoming ? (p.$theme?.text || '#e8b96b') : 'rgba(255, 255, 255, 0.65)')};
    font-size: 13px;
    font-weight: ${p => (p.$isUpcoming ? '600' : 'normal')};
    margin-top: 6px;
`;
const Content = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
    overflow-wrap: anywhere;

    @media (min-width: 721px) { padding-left: 0; padding-right: 64px; }
    @media (max-width: 720px) { padding: 8px 16px 24px; }
`;
const Type = styled.div`display:flex;align-items:center;gap:7px;color:${p => p.$color};font-size:12px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;`;
const Title = styled.h2`margin:8px 0 0;color:white;font-size:27px;line-height:1.15;`;
const StatusButton = styled.button`display:inline-flex;align-items:center;gap:7px;margin-top:14px;padding:8px 11px;border:1px solid ${p => p.$completed ? p.$color : 'rgba(255,255,255,.16)'};border-radius:999px;background:${p => p.$completed ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.06)'};color:${p => p.$completed ? p.$color : 'rgba(255,255,255,.88)'};cursor:pointer;font:inherit;font-size:12px;font-weight:700;&:disabled{opacity:.6;cursor:default;}`;
const Summary = styled.p`margin:14px 0 0;color:rgba(255,255,255,.68);font-size:14px;line-height:1.55;`;
const Progress = styled.section`margin-top:20px;padding-top:18px;border-top:1px solid rgba(255,255,255,.12);`;
const ProgressLabel = styled.div`margin-bottom:9px;color:rgba(255,255,255,.62);font-size:13px;`;
const StateButtons = styled.div`display:flex;flex-wrap:wrap;gap:8px;`;
const StateButton = styled.button`border:1px solid ${p => p.$selected ? '#83b8ba' : 'rgba(255,255,255,.17)'};border-radius:999px;padding:8px 11px;background:${p => p.$selected ? 'rgba(105,185,187,.2)' : 'rgba(255,255,255,.04)'};color:rgba(255,255,255,.9);font-size:13px;cursor:pointer;&:disabled{opacity:.65;cursor:default;}`;
const Sections = styled.div`section+section{margin-top:22px;}`;
const SectionTitle = styled.h3`margin:0 0 10px;color:rgba(255,255,255,.64);font-size:12px;letter-spacing:.04em;text-transform:uppercase;`;
const Entry = styled.div`display:flex;gap:9px;padding:8px 0;color:${p => p.$checked ? 'rgba(255,255,255,.44)' : 'rgba(255,255,255,.87)'};text-decoration:${p => p.$checked ? 'line-through' : 'none'};font-size:14px;line-height:1.45;`;
const CheckButton = styled.button`flex:0 0 auto;padding:0;border:0;background:transparent;color:${p => p.$color};cursor:pointer;&:disabled{opacity:.55;cursor:default;}`;
const Decoration = styled.span`display:inline-block;flex:0 0 auto;min-width:1.35em;margin-right:4px;text-decoration:none;`;
const Quantity = styled.span`margin-right:4px;color:white;font-weight:700;`;
const EntryDetail = styled.div`margin-top:3px;color:rgba(255,255,255,.53);font-size:12px;line-height:1.4;text-decoration:none;`;
const Metadata = styled.div`
    > div {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 10px 0;
        color: rgba(255,255,255,.55);
        font-size: 12px;
        > span { min-width: 0; flex: 0 1 40%; }
        > strong { min-width: 0; flex: 0 1 60%; }
    }
    > div:first-child { padding-top: 0; }
    > div:last-child { padding-bottom: 0; }
    > div + div { border-top: 1px solid rgba(255,255,255,.08); }
    strong { color: rgba(255,255,255,.86); text-align: right; overflow-wrap: anywhere; }
`;
const ImdbLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    color: ${ENTITY_COLORS.place.text};
    text-decoration: none;
    > span { min-width: 0; overflow-wrap: anywhere; }
    &:hover { text-decoration: underline; }
    &:focus-visible { outline: 2px solid ${ENTITY_COLORS.place.text}; outline-offset: 3px; }
`;
const MetadataLink = styled.a`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    box-sizing: border-box;
    padding: 11px 14px;
    border: 1px solid rgba(107,200,200,.3);
    border-radius: 10px;
    color: ${ENTITY_COLORS.place.text};
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    > svg { flex-shrink: 0; }
    &:hover { background: rgba(107,200,200,.08); }
    &:focus-visible { outline: 2px solid ${ENTITY_COLORS.place.text}; outline-offset: 3px; }
`;
const MentionedIn = styled.section`min-width:0;`;
const SourceMessageCard = styled.div`& > div { margin: 0; }`;
const SourceMessageLoading = styled.div`padding:12px;color:rgba(255,255,255,.55);font-size:13px;`;
const SourceLink = styled.a`align-self:flex-start;display:inline-flex;align-items:center;gap:7px;color:${ENTITY_COLORS.place.text};font-size:13px;font-weight:600;text-decoration:none;`;

export default ArtifactDetailModal;
